import * as React from "react";
import { useEffect } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { Alert, Badge, Button, Dialog } from "@/components/ui";
import { FormResponse } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { respondentName, submittedIdentityDifference } from "../responseIdentity";
import { renderFieldValue, responseDuration } from "./ResponseAnswer";
import { ResponseSourceDetails } from "./ResponseSourceDetails";

const STATUS_BADGES: Record<
  string,
  { label: string; variant: "outline" | "default" | "secondary"; className: string }
> = {
  initialized: { label: "Initialized", variant: "outline", className: "" },
  in_progress: { label: "In Progress", variant: "secondary", className: "bg-sky-50 text-sky-700 border-sky-200" },
  completed: { label: "Completed", variant: "default", className: "bg-emerald-500 border-emerald-500 text-white" },
};

/** The response's status, plus Test Run, as the table and the details show them. */
export function ResponseStatusBadges({ status, isTest }: Pick<FormResponse, "status" | "isTest">) {
  const badge = STATUS_BADGES[status || "initialized"] ?? STATUS_BADGES.initialized;
  return (
    <>
      <Badge variant={badge.variant} className={cn("text-[10px] px-1.5 py-0.5 font-bold uppercase", badge.className)}>
        {badge.label}
      </Badge>
      {isTest && (
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0.5 font-bold uppercase text-amber-600 border-amber-200 bg-amber-50/10"
        >
          Test Run
        </Badge>
      )}
    </>
  );
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface ResponseDetailsDialogProps {
  /** The responses on the table's current page, so Previous and Next can step through them. */
  responses: FormResponse[];
  responseId: number | null;
  onResponseIdChange: (id: number | null) => void;
  formName?: string;
  canMove: boolean;
  onMove: (response: FormResponse) => void;
}

/** One response in full: who answered, their answers, and where the link came from. */
export function ResponseDetailsDialog({
  responses,
  responseId,
  onResponseIdChange,
  formName,
  canMove,
  onMove,
}: ResponseDetailsDialogProps) {
  const index = responses.findIndex(r => r.id === responseId);
  const response = index >= 0 ? responses[index] : null;
  const previous = index > 0 ? responses[index - 1] : null;
  const next = index >= 0 && index < responses.length - 1 ? responses[index + 1] : null;

  useEffect(() => {
    if (!response) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable='true']")) return;
      if (event.key === "ArrowLeft" && previous) onResponseIdChange(previous.id);
      if (event.key === "ArrowRight" && next) onResponseIdChange(next.id);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [response, previous, next, onResponseIdChange]);

  if (!response) return null;

  const respondent = respondentName(response);
  const typedDifference = submittedIdentityDifference(response);
  const finishedAt = response.completedAt ? formatDateTime(response.completedAt) : null;
  const startedAt = formatDateTime(response.createdAt);
  const duration = responseDuration(response.createdAt, response.completedAt);
  const summary = [
    formName,
    finishedAt ?? (startedAt && `Started ${startedAt}`),
    duration && `took ${duration}`,
  ].filter(Boolean);
  const people = [
    response.fan?.email,
    response.fan?.phoneNumber,
    response.player?.fullName ? `Player: ${response.player.fullName}` : "No player",
  ].filter(Boolean);
  const answers = response.formFieldResponses || [];

  return (
    <Dialog open onOpenChange={open => !open && onResponseIdChange(null)} size="lg" showCloseButton>
      <Dialog.Header className="pr-12">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <Dialog.Title className="flex flex-wrap items-baseline gap-x-2 text-base font-bold font-sans">
                <span>{respondent?.name ?? "Anonymous"}</span>
                {respondent?.fromForm && <span className="text-xs font-normal text-slate-400">from the form</span>}
              </Dialog.Title>
              {canMove && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-primary hover:underline cursor-pointer"
                  onClick={() => onMove(response)}
                >
                  {respondent ? "Wrong Fan?" : "Add Fan"}
                </button>
              )}
            </div>
            {summary.length > 0 && (
              <Dialog.Description className="text-xs text-slate-500">{summary.join(" · ")}</Dialog.Description>
            )}
            <p className="text-xs text-slate-500 break-words">{people.join(" · ")}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <ResponseStatusBadges status={response.status} isTest={response.isTest} />
            {responses.length > 1 && (
              <div className="ml-1 flex items-center">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Previous response"
                  disabled={!previous}
                  onClick={() => previous && onResponseIdChange(previous.id)}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Next response"
                  disabled={!next}
                  onClick={() => next && onResponseIdChange(next.id)}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </Dialog.Header>

      <Dialog.Content className="max-h-[calc(100vh-16rem)] overflow-y-auto space-y-4 py-4 px-6">
        {typedDifference && (
          <Alert variant="warning" data-testid="submitted-identity-callout">
            <AlertTriangle />
            <Alert.Description>
              <span>
                Submitted as <strong>{typedDifference.join(" · ")}</strong>, which differs from{" "}
                {response.fan?.displayName ? `${response.fan.displayName}'s` : "this"} Fan record.
                {/* The pointer only makes sense for those who see the link */}
                {canMove && (
                  <>
                    {" "}
                    If someone else answered, use <em>Wrong Fan?</em> above.
                  </>
                )}
              </span>
            </Alert.Description>
          </Alert>
        )}

        {answers.length ? (
          <ol className="divide-y divide-slate-100" aria-label="Answers">
            {answers.map((fieldResp, idx) => (
              <li key={fieldResp.id || idx} className="flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0">
                <span className="text-[13px] font-medium text-slate-600">
                  {fieldResp.formField?.title || fieldResp.formField?.fieldKey || `Question ${idx + 1}`}
                </span>
                <div className="font-sans text-sm leading-normal">{renderFieldValue(fieldResp)}</div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm italic text-slate-400">No answers yet.</p>
        )}

        <ResponseSourceDetails response={response} />
      </Dialog.Content>

      <Dialog.Footer>
        <Button onClick={() => onResponseIdChange(null)} type="button">
          Close
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
